// SEPA-Lastschriftmandat als PDF (Handoff Eigentümer §7, Bank & SEPA „Mandat als PDF“).
// Inhalt als reine Funktion (testbar), Ausgabe über jsPDF.

import jsPDF from 'jspdf';
import { deDatum } from '../immobilien/objektDetail';
import { VERWALTUNG_ABSENDER } from '../rechnungen/rechnungsanschrift';

export type Rhythmus = 'monatlich' | 'quartalsweise' | 'jaehrlich';
export const RHYTHMEN: readonly { key: Rhythmus; label: string }[] = [
  { key: 'monatlich', label: 'Monatlich' }, { key: 'quartalsweise', label: 'Quartalsweise' }, { key: 'jaehrlich', label: 'Jährlich' },
];
export const istRhythmus = (v: unknown): v is Rhythmus => v === 'monatlich' || v === 'quartalsweise' || v === 'jaehrlich';

export interface MandatDaten {
  glaeubigerId: string;
  mandatsreferenz: string;
  mandatsdatum: string;
  kundennummer: string;
  kontoinhaber: string;
  anschrift: string;
  iban: string;
  bic: string;
}

/** Was für ein gültiges Mandat fehlt (leer = PDF möglich). */
export function mandatFehlt(d: MandatDaten): string[] {
  return [
    !d.glaeubigerId.trim() && 'Gläubiger-ID',
    !d.mandatsreferenz.trim() && 'Mandatsreferenz',
    !d.mandatsdatum && 'Mandatsdatum',
    !d.kontoinhaber.trim() && 'Kontoinhaber',
    !d.iban.replace(/\s+/g, '') && 'IBAN',
  ].filter((x): x is string => !!x);
}

const iban4 = (s: string) => s.replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

/** Zeilen des Mandats (Muster der Deutschen Kreditwirtschaft, wiederkehrende Zahlung). */
export interface Empfaenger {
  /** im Ermächtigungstext: „Immobilienmanagement Schwede“ bzw. „die WEG Hangweg 5“ */
  name: string;
  /** Zeile „Zahlungsempfänger“ */
  anschrift: string;
}

/**
 * Zahlungsempfänger: bei WEG-Hausgeld die Gemeinschaft (GdWE), vertreten durch die Verwaltung;
 * sonst die Verwaltung selbst (Freigabe 14.10.2026).
 */
export function mandatEmpfaenger(wegNamen: readonly string[], verwaltung: readonly string[] = VERWALTUNG_ABSENDER): Empfaenger {
  const [vName = '', ...vAdresse] = verwaltung.map(z => z.trim()).filter(Boolean);
  const wegs = [...new Set(wegNamen.map(n => n.trim()).filter(Boolean))].map(n => (/^weg\b/i.test(n) ? n : `WEG ${n}`));
  if (wegs.length === 0) return { name: vName || 'den Zahlungsempfänger', anschrift: [vName, ...vAdresse].filter(Boolean).join(', ') || '–' };
  const gemeinschaft = wegs.join(' / ');
  return {
    name: `die ${gemeinschaft}${vName ? `, vertreten durch ${vName}` : ''}`,
    anschrift: [gemeinschaft, vName ? `vertreten durch ${vName}` : '', ...vAdresse].filter(Boolean).join(', '),
  };
}

export function mandatInhalt(d: MandatDaten, empfaenger: Empfaenger = mandatEmpfaenger([])): { titel: string; kopf: [string, string][]; text: string[]; zahler: [string, string][]; fuss: string } {
  const name = empfaenger.name;
  // „die von … gezogenen Lastschriften“ – bei der WEG ohne Artikel-Dopplung
  const von = name.replace(/^die /, 'der ').replace(/, vertreten durch .*$/, '');
  return {
    titel: 'SEPA-Lastschriftmandat',
    kopf: [
      ['Zahlungsempfänger', empfaenger.anschrift],
      ['Gläubiger-Identifikationsnummer', d.glaeubigerId.trim()],
      ['Mandatsreferenz', d.mandatsreferenz.trim()],
      ...(d.kundennummer.trim() ? [['Kunden-/Objektnummer', d.kundennummer.trim()] as [string, string]] : []),
      ['Zahlungsart', 'Wiederkehrende Zahlung'],
    ],
    text: [
      `Ich ermächtige ${name}, Zahlungen von meinem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein Kreditinstitut an, die von ${von} auf mein Konto gezogenen Lastschriften einzulösen.`,
      'Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem Kreditinstitut vereinbarten Bedingungen.',
    ],
    zahler: [
      ['Kontoinhaber (Zahlungspflichtiger)', d.kontoinhaber.trim()],
      ['Anschrift', d.anschrift.trim() || '–'],
      ['IBAN', iban4(d.iban)],
      ['BIC', d.bic.trim().toUpperCase() || '–'],
      ['Datum des Mandats', d.mandatsdatum ? deDatum(d.mandatsdatum) : '–'],
    ],
    fuss: 'Ort, Datum und Unterschrift des Kontoinhabers',
  };
}

export function mandatDateiname(d: Pick<MandatDaten, 'mandatsreferenz'>): string {
  return `SEPA-Mandat_${d.mandatsreferenz.trim().replace(/[^\w.-]+/g, '_') || 'ohne-Referenz'}.pdf`;
}

/** Erzeugt das PDF und lädt es herunter. */
export function ladeMandatPdf(d: MandatDaten, empfaenger: Empfaenger = mandatEmpfaenger([])): void {
  const inhalt = mandatInhalt(d, empfaenger);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const rand = 20;
  const breite = pdf.internal.pageSize.getWidth() - rand * 2;
  let y = 28;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text(inhalt.titel, rand, y);
  y += 12;
  const tabelle = (zeilen: [string, string][]) => {
    pdf.setFontSize(10);
    for (const [k, v] of zeilen) {
      pdf.setFont('helvetica', 'normal');
      pdf.text(k, rand, y);
      pdf.setFont('helvetica', 'bold');
      // lange Werte (Anschrift des Zahlungsempfängers) umbrechen
      const werte = pdf.splitTextToSize(v || '–', breite - 70) as string[];
      pdf.text(werte, rand + 70, y);
      y += 7 + (werte.length - 1) * 5;
    }
  };
  tabelle(inhalt.kopf);
  y += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  for (const absatz of inhalt.text) {
    const zeilen = pdf.splitTextToSize(absatz, breite) as string[];
    pdf.text(zeilen, rand, y);
    y += zeilen.length * 5 + 4;
  }
  y += 4;
  tabelle(inhalt.zahler);
  y += 28;
  pdf.setDrawColor(120);
  pdf.line(rand, y, rand + 110, y);
  pdf.setFontSize(9);
  pdf.text(inhalt.fuss, rand, y + 5);
  pdf.save(mandatDateiname(d));
}
